// This file and logic was moved from a previous commit during A2A refactor.
// See Here: https://github.com/nbcnews/ramen/pull/2802/files#diff-29bda66271afa7cbaebe6ab6f53d16f4
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';

import BTE from 'lib/BTE';

import { stub as $t } from '@nbcnews/analytics-framework';

const trackingEvents = ['mbt_scrolled_percentage'];

$t('register', trackingEvents, { allowDuplicate: true });

/**
 * ScrollingAnalytics tracks user scroll position through content
 * and emits analytics events at intervals of 25%, 50%, and 75%
 *
 * @param {object} props
 * @param {string} props.contentType - Type of content being tracked ('article' for example)
 * @param {string} props.contentUrl - URL of the content being tracked
 * @param {React.ReactNode} props.children - Child elements to render inside the component wrapper
 * @returns {React.ReactNode} A wrapper that tracks scroll analytics for its children
 */
const ScrollingAnalytics = ({ contentType, contentUrl, children }) => {
  const [, setScrolledPercentages] = useState({});

  const wrapperRef = useRef(null);


  const trackScrollPercentage = useCallback(() => {
    if (!wrapperRef.current) {
      return;
    }

    const { top, bottom } = wrapperRef.current.getBoundingClientRect();

    // NO-OP if we've already emitted the events for this page
    // or if the user scrolled past the first article and hit refresh
    const percentage = (Math.abs(top) / (Math.abs(top) + bottom)) * 100;

    setScrolledPercentages((prev) => {
      // eslint-disable-next-line camelcase
      const { scrolled_25, scrolled_50, scrolled_75 } = prev;

      // eslint-disable-next-line camelcase
      if (percentage > 100 && scrolled_25 && scrolled_50 && scrolled_75) {
        return prev;
      }

      const newState = { ...prev };
      let hasChanges = false;

      // Check and emit 25%
      if (!prev.scrolled_25 && percentage >= 25) {
        $t('track', 'mbt_scrolled_percentage', {
          type: contentType,
          url: contentUrl,
          percent: 25,
        });
        newState.scrolled_25 = true;
        hasChanges = true;
      }

      // Check and emit 50%
      if (!prev.scrolled_50 && percentage >= 50) {
        $t('track', 'mbt_scrolled_percentage', {
          type: contentType,
          url: contentUrl,
          percent: 50,
        });
        newState.scrolled_50 = true;
        hasChanges = true;
      }

      // Check and emit 75%
      if (!prev.scrolled_75 && percentage >= 75) {
        $t('track', 'mbt_scrolled_percentage', {
          type: contentType,
          url: contentUrl,
          percent: 75,
        });
        newState.scrolled_75 = true;
        hasChanges = true;
      }

      return hasChanges ? newState : prev;
    });
  }, [contentType, contentUrl]);

  useEffect(() => {
    BTE.on('scroll', trackScrollPercentage, 'throttle');

    return () => {
      BTE.remove('scroll', trackScrollPercentage, 'throttle');
    };
  }, [trackScrollPercentage]);

  return (
    <div ref={wrapperRef}>
      {children}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default ScrollingAnalytics;
